import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { fullErrorText } from '@/api/errors';
import { FieldSearch, buildSearchParams, type SearchFieldOption } from '@/components/FieldSearch';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { applicationApi } from '@/api/application.api';
import { create } from '@bufbuild/protobuf';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { ConfigSyncStatus, InstanceStatus } from '@/proto/agentplatform/v1/instance_pb';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PencilIcon, TrashIcon, LoaderIcon, PlusIcon } from '@/components/icons';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';

/** 表格单元格常有 overflow:hidden，原生 title 不可靠；用 portal 固定定位展示说明 */
function HoverTooltip({ text, children }: { text: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const updatePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxTooltipPx = 320;
    const pad = 8;
    let left = r.left;
    const vw = window.innerWidth;
    if (left + maxTooltipPx + pad > vw) {
      left = Math.max(pad, vw - maxTooltipPx - pad);
    }
    setPos({ top: r.bottom + 6, left });
  }, []);
  useEffect(() => {
    if (!open) return;
    updatePos();
    const onMove = () => updatePos();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, updatePos]);
  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex shrink-0 cursor-default items-center justify-center rounded p-0.5 text-muted-foreground outline-none hover:bg-muted/70 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        tabIndex={0}
        aria-label={`说明：${text}`}
        onMouseEnter={() => {
          updatePos();
          setOpen(true);
        }}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => {
          updatePos();
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>
      {open
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none fixed z-[200] max-w-sm rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs leading-snug text-popover-foreground shadow-md [max-width:min(20rem,calc(100vw-1rem))] whitespace-pre-wrap break-words"
              style={{ top: pos.top, left: pos.left }}
            >
              {text}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function formatTimestamp(timestamp: number | bigint | undefined) {
  if (!timestamp) return '-';
  const date = new Date(Number(timestamp) / 1000000);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

const INSTANCE_SEARCH_FIELDS: SearchFieldOption[] = [
  { label: '名称', key: 'name', match: 'fuzzy' },
  { label: '描述', key: 'description', match: 'fuzzy' },
  { label: 'ID', key: 'id', match: 'filter' },
  { label: 'Agent ID', key: 'agent_id', match: 'filter' },
];

export default function ApplicationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchField, setSearchField] = useState('name');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [deleteUid, setDeleteUid] = useState<string | null>(null);

  // 关键字变化后回到首页，避免停留在超出范围的页码
  useEffect(() => {
    setPage(1);
  }, [searchKeyword]);

  const searchParams = buildSearchParams(searchField, searchKeyword, INSTANCE_SEARCH_FIELDS);
  const searchKey = JSON.stringify(searchParams);

  const { data, isLoading } = useQuery({
    queryKey: ['applications', page, pageSize, searchKey],
    queryFn: () =>
      applicationApi.list(
        create(ListRequestSchema, {
          page,
          pageSize,
          fuzzy: searchParams.fuzzy,
          filter: searchParams.filter,
        })
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: (uid: string) => applicationApi.delete(uid),
    onSuccess: () => {
      setDeleteUid(null);
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError: (error) => {
      toast.error(`删除失败：${fullErrorText(error)}`);
    },
  });

  const applications = (data?.data || []).filter(Boolean);

  const totalCount = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">智能体实例</h2>
          <p className="text-sm text-muted-foreground mt-1">
            基于智能体模版创建的运行实例，可独立配置参数
          </p>
        </div>
        <div className="flex items-center gap-3">
          <FieldSearch
            fields={INSTANCE_SEARCH_FIELDS}
            field={searchField}
            onFieldChange={setSearchField}
            keyword={searchKeyword}
            onKeywordChange={setSearchKeyword}
          />
          <Button onClick={() => navigate('/applications/create')} className="gap-2">
            <PlusIcon className="h-4 w-4" />
            创建实例
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="border rounded-lg">
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </div>
        </div>
      ) : applications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center border rounded-lg">
          <svg
            className="w-12 h-12 text-muted-foreground mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
            />
          </svg>
          <h3 className="text-lg font-semibold mb-1">暂无应用</h3>
          <p className="text-sm text-muted-foreground">创建第一个应用来开始</p>
        </div>
      ) : (
        <div>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>名称</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>描述</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>创建时间</TableHead>
                  <TableHead>更新时间</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applications.map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      navigate(`/agents/${item.agentId}/apps/${item.id}`, {
                        state: { returnTo: '/applications' },
                      });
                    }}
                  >
                    <TableCell className="font-medium py-4">
                      <div className="min-w-0">
                        <button
                          type="button"
                          className="text-primary hover:text-primary/80 font-medium text-left truncate block"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/agents/${item.agentId}/apps/${item.id}`, {
                              state: { returnTo: '/applications' },
                            });
                          }}
                        >
                          {item.name}
                        </button>
                        <span className="text-xs text-muted-foreground/60">ID: {item.id}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="inline-flex items-center gap-1.5">
                        {item.status === InstanceStatus.RUNNING ? (
                          <>
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                              <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                              运行中
                            </span>
                            {item.configSyncStatus === ConfigSyncStatus.OUTDATED && (
                              <HoverTooltip text="智能体模版更新，实例需手动更新">
                                <svg
                                  className="h-3.5 w-3.5 text-foreground"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              </HoverTooltip>
                            )}
                          </>
                        ) : item.status === InstanceStatus.PENDING ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                            <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                            待启动
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">
                      {item.description || '-'}
                    </TableCell>
                    <TableCell>{item.agentId || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatTimestamp(item.createdAt)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatTimestamp(item.updatedAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => navigate(`/applications/${item.id}/edit`)}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeleteUid(item.id)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={7} className="bg-muted/30">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">共 {totalCount} 条</span>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-muted-foreground">每页</span>
                          <select
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(Number(e.target.value));
                              setPage(1);
                            }}
                            className="h-7 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <span className="text-sm text-muted-foreground">条</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 19l-7-7 7-7"
                              />
                            </svg>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteUid} onOpenChange={() => setDeleteUid(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>确定要删除该应用吗？此操作不可撤销。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteUid(null)}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteUid && deleteMutation.mutate(deleteUid)}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
